import React, { useState, useEffect } from 'react';
import {
    Typography,
    Paper,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Box,
    Grid,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormLabel,
    TextField,
    useTheme,
    useMediaQuery,
    CircularProgress
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { workingHoursService } from '../../services/appointmentService';
import type { WorkingHours } from '../../services/appointmentService';
import { TimeSlot } from '../../types/timeSlot';
import { timeSlotService } from '../../services/appointmentService';
import { TimePicker, DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { errorHandler } from '../../utils/errorHandler';

const defaultWorkingHours = {
    start: '13:00',
    end: '22:00'
};

const TimeSlotsAdmin: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingWorkingHours, setLoadingWorkingHours] = useState(true);
    const [addDialog, setAddDialog] = useState(false);
    const [workingHoursDialog, setWorkingHoursDialog] = useState(false);
    const [newTimeRange, setNewTimeRange] = useState<{
        date: Date | null;
        start: Date | null;
        end: Date | null;
        interval: 30 | 60;
    }>({
        date: null,
        start: null,
        end: null,
        interval: 30
    });
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        id: string | null;
        action: 'delete' | 'status' | 'delete_working_hours' | 'delete_all_working_hours' | 'delete_all_time_slots';
        isActive?: boolean;
        time?: string;
        isDefault?: boolean;
    }>({
        open: false,
        id: null,
        action: 'delete'
    });
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [workingHours, setWorkingHours] = useState(defaultWorkingHours);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [workingHoursList, setWorkingHoursList] = useState<Array<{
        id: string;
        date: string;
        start: string;
        end: string;
        isDefault: boolean;
    }>>([]);
    const [error, setError] = useState<string | null>(null);

    const columns: GridColDef[] = [
        {
            field: 'date',
            headerName: 'Tarih',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Typography>
                    {format(new Date(params.value), 'yyyy-MM-dd')}
                </Typography>
            )
        },
        {
            field: 'time',
            headerName: 'Saat',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Typography>{params.value}</Typography>
            )
        },
        {
            field: 'isActive',
            headerName: 'Durum',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Box display="flex" alignItems="center">
                    <Switch
                        checked={params.value}
                        onChange={() => setConfirmDialog({
                            open: true,
                            id: params.row.id,
                            action: 'status',
                            isActive: !params.value,
                            time: params.row.time
                        })}
                        color="primary"
                    />
                    <Typography>{params.value ? 'Aktif' : 'Pasif'}</Typography>
                </Box>
            )
        },
        {
            field: 'createdAt',
            headerName: 'Oluşturulma Tarihi',
            flex: 1,
            renderCell: (params) => {
                const date = new Date(params.value);
                return date.toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        },
        {
            field: 'actions',
            headerName: 'İşlemler',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Sil">
                        <IconButton
                            color="error"
                            onClick={() => setConfirmDialog({
                                open: true,
                                id: params.row.id,
                                action: 'delete',
                                time: params.row.time
                            })}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    const workingHoursColumns: GridColDef[] = [
        {
            field: 'isDefault',
            headerName: 'Tür',
            flex: 1,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Varsayılan' : 'Özel Gün'}
                    color={params.value ? 'primary' : 'secondary'}
                    variant="outlined"
                />
            )
        },
        {
            field: 'date',
            headerName: 'Tarih',
            flex: 1,
            renderCell: (params) => (
                <Typography>
                    {params.row.isDefault ? 'Her Gün' : format(new Date(params.value), 'dd.MM.yyyy')}
                </Typography>
            )
        },
        {
            field: 'start',
            headerName: 'Başlangıç',
            flex: 1,
            renderCell: (params) => (
                <Typography>{params.value}</Typography>
            )
        },
        {
            field: 'end',
            headerName: 'Bitiş',
            flex: 1,
            renderCell: (params) => (
                <Typography>{params.value}</Typography>
            )
        },
        {
            field: 'actions',
            headerName: 'İşlemler',
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={params.row.isDefault ? "Varsayılan saatleri sıfırla" : "Sil"}>
                        <IconButton
                            color="error"
                            onClick={() => setConfirmDialog({
                                open: true,
                                id: params.row.id,
                                action: 'delete_working_hours',
                                isDefault: params.row.isDefault
                            })}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    const loadTimeSlots = async () => {
        try {
            setLoading(true);
            const data = await timeSlotService.getTimeSlots();
            setTimeSlots(data.map(slot => ({
                ...slot,
                date: format(new Date(slot.date), 'yyyy-MM-dd')
            })));
            setError(null);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadWorkingHours = async () => {
        try {
            setLoadingWorkingHours(true);
            const hours = await workingHoursService.getWorkingHours();
            if (hours) {
                setWorkingHours(hours);
            }
        } catch (error) {
            errorHandler.handleError(error, 'Error loading working hours');
            setSnackbar({
                open: true,
                message: 'Çalışma saatleri yüklenirken bir hata oluştu',
                severity: 'error'
            });
        } finally {
            setLoadingWorkingHours(false);
        }
    };

    const loadWorkingHoursList = async () => {
        try {
            const hours = await workingHoursService.getWorkingHours();
            errorHandler.log('Working hours data loaded');
            
            const hoursList = [{
                id: 'default',
                date: format(new Date(), 'yyyy-MM-dd'),
                start: hours.start,
                end: hours.end,
                isDefault: true,
                isActive: true
            }];
            
            errorHandler.log('Working hours list processed');
            setWorkingHoursList(hoursList);
        } catch (error) {
            errorHandler.handleError(error, 'Error loading working hours list');
            setError('Çalışma saatleri listesi yüklenirken bir hata oluştu.');
        }
    };

    const handleDeleteWorkingHours = async (id: string) => {
        try {
            const docRef = doc(db, 'settings', 'workingHours');
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                const newData = { ...data };
                
                if (id === 'default') {
                    // Varsayılan saatleri silme, sadece sıfırla
                    newData.default = {
                        start: '13:00',
                        end: '22:00'
                    };
                } else {
                    // Özel günü sil
                    if (newData.specificDays) {
                        delete newData.specificDays[id];
                    }
                }
                
                await setDoc(docRef, newData);
                loadWorkingHoursList();
                
                setSnackbar({
                    open: true,
                    message: id === 'default' ? 'Varsayılan saatler sıfırlandı' : 'Özel gün silindi',
                    severity: 'success'
                });
            }
        } catch (error) {
            errorHandler.handleError(error, 'Error deleting working hours');
            setSnackbar({
                open: true,
                message: 'Çalışma saatleri silinirken bir hata oluştu',
                severity: 'error'
            });
        }
    };

    const handleDeleteAllWorkingHours = async () => {
        try {
            const docRef = doc(db, 'settings', 'workingHours');
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                // Varsayılan saatleri sıfırla, özel günleri temizle
                await setDoc(docRef, {
                    ...data,
                    default: {
                        start: '13:00',
                        end: '22:00'
                    },
                    specificDays: {}
                });
                
                loadWorkingHoursList();
                setSnackbar({
                    open: true,
                    message: 'Tüm özel çalışma saatleri kaldırıldı',
                    severity: 'success'
                });
                setConfirmDialog(prev => ({ ...prev, open: false }));
            }
        } catch (error) {
            errorHandler.handleError(error, 'Error deleting all working hours');
            setSnackbar({
                open: true,
                message: 'Çalışma saatleri silinirken bir hata oluştu',
                severity: 'error'
            });
        }
    };

    const handleDeleteAllTimeSlots = async () => {
        try {
            const timeSlotsRef = collection(db, 'timeSlots');
            const querySnapshot = await getDocs(timeSlotsRef);
            
            // Tüm dökümanları sil
            const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);
            
            loadTimeSlots();
            setSnackbar({
                open: true,
                message: 'Tüm ekstra randevu saatleri kaldırıldı',
                severity: 'success'
            });
            setConfirmDialog(prev => ({ ...prev, open: false }));
        } catch (error) {
            errorHandler.handleError(error, 'Error deleting all time slots');
            setSnackbar({
                open: true,
                message: 'Randevu saatleri silinirken bir hata oluştu',
                severity: 'error'
            });
        }
    };

    useEffect(() => {
        const init = async () => {
            try {
                await loadWorkingHours();
                await loadWorkingHoursList();
                await loadTimeSlots();
            } catch (error) {
                console.error('Initialization error:', error);
            }
        };
        init();
    }, []);

    const handleAddTimeSlots = async () => {
        if (!newTimeRange.date || !newTimeRange.start || !newTimeRange.end) {
            setSnackbar({
                open: true,
                message: 'Lütfen tüm alanları doldurun',
                severity: 'error'
            });
            return;
        }

        try {
            const formattedDate = format(newTimeRange.date, 'yyyy-MM-dd');
            
            // Başlangıç ve bitiş saatlerini aynı gün üzerinde ayarla
            const baseDate = new Date(newTimeRange.date);
            const startTime = new Date(newTimeRange.start);
            const endTime = new Date(newTimeRange.end);
            
            // Başlangıç saatini ayarla
            const startDateTime = new Date(baseDate);
            startDateTime.setHours(startTime.getHours());
            startDateTime.setMinutes(startTime.getMinutes());
            
            // Bitiş saatini ayarla
            const endDateTime = new Date(baseDate);
            endDateTime.setHours(endTime.getHours());
            endDateTime.setMinutes(endTime.getMinutes());

            // Create time slots based on interval
            const slots: Array<Omit<TimeSlot, 'id'>> = [];
            let currentTime = startDateTime;

            errorHandler.log('Creating time slots', {
                formattedDate,
                startDateTime,
                endDateTime,
                interval: newTimeRange.interval
            });

            while (currentTime < endDateTime) {
                const timeString = format(currentTime, 'HH:mm');
                errorHandler.log('Adding time slot', timeString);
                
                slots.push({
                    date: formattedDate,
                    time: timeString,
                    status: 'available',
                    isActive: true
                });
                
                currentTime = new Date(currentTime.getTime() + newTimeRange.interval * 60000);
            }

            errorHandler.log('Created time slots', slots);

            if (slots.length === 0) {
                throw new Error('Seçilen zaman aralığında slot oluşturulamadı');
            }

            await timeSlotService.addTimeSlots(slots);
            setSnackbar({
                open: true,
                message: 'Randevu saatleri başarıyla eklendi',
                severity: 'success'
            });
            setAddDialog(false);
            loadTimeSlots();
        } catch (err: any) {
            errorHandler.handleError(err, 'Error adding time slots');
            setSnackbar({
                open: true,
                message: err.message || 'Randevu saatleri eklenirken bir hata oluştu',
                severity: 'error'
            });
        }
    };

    const handleStatusChange = async () => {
        if (!confirmDialog.id || confirmDialog.isActive === undefined) return;

        try {
            await timeSlotService.updateTimeSlotStatus(confirmDialog.id, confirmDialog.isActive);
            setSnackbar({
                open: true,
                message: 'Randevu saati durumu başarıyla güncellendi',
                severity: 'success'
            });
            loadTimeSlots();
        } catch (error) {
            errorHandler.handleError(error, 'Error updating time slot status');
            setSnackbar({
                open: true,
                message: 'Randevu saati durumu güncellenirken bir hata oluştu',
                severity: 'error'
            });
        } finally {
            setConfirmDialog(prev => ({ ...prev, open: false }));
        }
    };

    const handleDelete = async () => {
        if (!confirmDialog.id) return;

        try {
            await timeSlotService.deleteTimeSlot(confirmDialog.id);
            setSnackbar({
                open: true,
                message: 'Randevu saati başarıyla silindi',
                severity: 'success'
            });
            loadTimeSlots();
        } catch (error) {
            console.error('Silme işlemi sırasında hata:', error);
            setSnackbar({
                open: true,
                message: 'Randevu saati silinirken bir hata oluştu',
                severity: 'error'
            });
        } finally {
            setConfirmDialog(prev => ({ ...prev, open: false }));
        }
    };

    const handleUpdateWorkingHours = async () => {
        if (!workingHours) {
            setSnackbar({
                open: true,
                message: 'Çalışma saatleri yüklenemedi',
                severity: 'error'
            });
            return;
        }

        try {
            // Create a copy of working hours to ensure type safety
            const updatedWorkingHours: WorkingHours = {
                ...workingHours
            };

            await workingHoursService.updateWorkingHours(updatedWorkingHours);
            setSnackbar({
                open: true,
                message: 'Çalışma saatleri başarıyla güncellendi',
                severity: 'success'
            });
            setSelectedDate(null);
            loadWorkingHours();
        } catch (error) {
            console.error('Çalışma saatleri güncellenirken hata:', error);
            setSnackbar({
                open: true,
                message: 'Çalışma saatleri güncellenirken bir hata oluştu',
                severity: 'error'
            });
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                gap: { xs: 2, sm: 0 },
                mb: 3 
            }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Randevu Saatleri
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                        Çalışma saatleri ve ekstra randevu saatlerinin yönetimi
                    </Typography>
                </Box>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2,
                    width: { xs: '100%', sm: 'auto' },
                    flexDirection: { xs: 'column', sm: 'row' }
                }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => {
                            setSelectedDate(null);
                            setWorkingHoursDialog(true);
                        }}
                        fullWidth={isMobile}
                    >
                        Çalışma Saatleri
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setAddDialog(true)}
                        fullWidth={isMobile}
                    >
                        Ekstra Saat Ekle
                    </Button>
                </Box>
            </Box>

            {/* Çalışma Saatleri Tablosu */}
            <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ 
                    p: 2, 
                    bgcolor: 'primary.main', 
                    color: 'primary.contrastText', 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}>
                    <Typography variant="h6">
                        Çalışma Saatleri
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setConfirmDialog({
                            open: true,
                            id: null,
                            action: 'delete_all_working_hours'
                        })}
                        sx={{ 
                            bgcolor: 'error.main', 
                            '&:hover': { bgcolor: 'error.dark' },
                            width: { xs: '100%', sm: 'auto' }
                        }}
                    >
                        Tüm Özel Günleri Kaldır
                    </Button>
                </Box>
                <Box sx={{ 
                    width: '100%', 
                    overflowX: 'auto',
                    '& .MuiDataGrid-root': {
                        minWidth: { xs: 600, sm: 'auto' }
                    }
                }}>
                    <DataGrid
                        rows={workingHoursList}
                        columns={workingHoursColumns.map(column => ({
                            ...column,
                            flex: isMobile ? undefined : column.flex,
                            width: isMobile ? 150 : undefined
                        }))}
                        autoHeight
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 5,
                                },
                            },
                            sorting: {
                                sortModel: [{ field: 'isDefault', sort: 'desc' }],
                            },
                        }}
                        disableRowSelectionOnClick
                        localeText={{
                            noRowsLabel: 'Çalışma saati kaydı bulunmadı',
                        }}
                    />
                </Box>
            </Paper>

            {/* Ekstra Saatler Tablosu */}
            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ 
                    p: 2, 
                    bgcolor: 'secondary.main', 
                    color: 'secondary.contrastText', 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}>
                    <Typography variant="h6">
                        Ekstra Randevu Saatleri
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setConfirmDialog({
                            open: true,
                            id: null,
                            action: 'delete_all_time_slots'
                        })}
                        sx={{ 
                            bgcolor: 'error.main', 
                            '&:hover': { bgcolor: 'error.dark' },
                            width: { xs: '100%', sm: 'auto' }
                        }}
                    >
                        Tümünü Kaldır
                    </Button>
                </Box>
                <Box sx={{ 
                    width: '100%', 
                    overflowX: 'auto',
                    '& .MuiDataGrid-root': {
                        minWidth: { xs: 600, sm: 'auto' }
                    }
                }}>
                    <DataGrid
                        rows={timeSlots}
                        columns={columns.map(column => ({
                            ...column,
                            flex: isMobile ? undefined : column.flex,
                            width: isMobile ? 150 : undefined,
                            // Mobil görünümde bazı sütunları gizle
                            hide: isMobile && column.field === 'createdAt'
                        }))}
                        loading={loading}
                        autoHeight
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                            sorting: {
                                sortModel: [{ field: 'date', sort: 'desc' }],
                            },
                        }}
                        disableRowSelectionOnClick
                        localeText={{
                            noRowsLabel: 'Randevu saati bulunamadı',
                        }}
                    />
                </Box>
            </Paper>

            {/* Ekstra Saat Ekleme Dialog */}
            <Dialog
                open={addDialog}
                onClose={() => {
                    setAddDialog(false);
                    setNewTimeRange({
                        date: null,
                        start: null,
                        end: null,
                        interval: 30
                    });
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Ekstra Randevu Saati Ekle</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Eklemek istediğiniz özel randevu tarihini ve saatini seçin.
                    </DialogContentText>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                                <DatePicker
                                    label="Tarih"
                                    value={newTimeRange.date}
                                    onChange={(newValue) => setNewTimeRange(prev => ({ ...prev, date: newValue }))}
                                    format="dd.MM.yyyy"
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Randevu Aralığı</FormLabel>
                                <RadioGroup
                                    row
                                    value={newTimeRange.interval}
                                    onChange={(e) => setNewTimeRange(prev => ({ ...prev, interval: parseInt(e.target.value) as 30 | 60 }))}
                                >
                                    <FormControlLabel value={30} control={<Radio />} label="30 Dakika" />
                                    <FormControlLabel value={60} control={<Radio />} label="1 Saat" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                                <TimePicker
                                    label="Başlangıç Saati"
                                    value={newTimeRange.start}
                                    onChange={(newValue) => setNewTimeRange(prev => ({ ...prev, start: newValue }))}
                                    views={['hours', 'minutes']}
                                    format="HH:mm"
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                                <TimePicker
                                    label="Bitiş Saati"
                                    value={newTimeRange.end}
                                    onChange={(newValue) => setNewTimeRange(prev => ({ ...prev, end: newValue }))}
                                    views={['hours', 'minutes']}
                                    format="HH:mm"
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button 
                        onClick={() => {
                            setAddDialog(false);
                            setNewTimeRange({
                                date: null,
                                start: null,
                                end: null,
                                interval: 30
                            });
                        }}
                        fullWidth={isMobile}
                    >
                        İptal
                    </Button>
                    <Button
                        onClick={handleAddTimeSlots}
                        color="primary"
                        variant="contained"
                        disabled={!newTimeRange.start || !newTimeRange.end || !newTimeRange.date}
                        fullWidth={isMobile}
                    >
                        Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Çalışma Saatleri Dialog */}
            <Dialog
                open={workingHoursDialog}
                onClose={() => {
                    setWorkingHoursDialog(false);
                    setSelectedDate(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Çalışma Saatlerini Düzenle</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Varsayılan çalışma saatlerini veya belirli bir gün için özel çalışma saatlerini ayarlayın.
                        Tarih seçilmezse varsayılan çalışma saatleri güncellenecektir.
                    </DialogContentText>
                    {loadingWorkingHours ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                                    <DatePicker
                                        label="Tarih (İsteğe bağlı - Özel gün için)"
                                        value={selectedDate}
                                        onChange={(newValue) => setSelectedDate(newValue)}
                                        format="dd.MM.yyyy"
                                        sx={{ width: '100%' }}
                                        slotProps={{
                                            textField: {
                                                placeholder: 'Tarih seçin (isteğe bağlı)',
                                                value: selectedDate ? undefined : '',
                                                error: false
                                            }
                                        }}
                                        defaultValue={null}
                                    />
                                </LocalizationProvider>
                                <Typography variant="caption" color="textSecondary">
                                    * Tarih seçilmezse, varsayılan çalışma saatleri güncellenecektir
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Başlangıç Saati"
                                    type="text"
                                    value={workingHours.start}
                                    onChange={(e) => {
                                        setWorkingHours({
                                            ...workingHours,
                                            start: e.target.value
                                        });
                                    }}
                                    fullWidth
                                    helperText="HH:mm formatında girin (örn: 09:00)"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Bitiş Saati"
                                    type="text"
                                    value={workingHours.end}
                                    onChange={(e) => {
                                        setWorkingHours({
                                            ...workingHours,
                                            end: e.target.value
                                        });
                                    }}
                                    fullWidth
                                    helperText="HH:mm formatında girin (örn: 18:00)"
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setWorkingHoursDialog(false);
                        setSelectedDate(null);
                    }}>İptal</Button>
                    <Button 
                        onClick={() => {
                            handleUpdateWorkingHours();
                            setWorkingHoursDialog(false);
                            setSelectedDate(null);
                        }}
                        color="primary" 
                        variant="contained"
                        disabled={loadingWorkingHours}
                    >
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Onay Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {confirmDialog.action === 'delete' ? 'Randevu Saati Silme' : 
                     confirmDialog.action === 'status' ? 'Durum Değiştirme' :
                     confirmDialog.action === 'delete_working_hours' ? 
                        (confirmDialog.isDefault ? 'Varsayılan Saatleri Sıfırlama' : 'Özel Gün Silme') :
                     confirmDialog.action === 'delete_all_working_hours' ? 'Tüm Özel Günleri Kaldır' :
                     'Tüm Randevu Saatlerini Kaldır'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.action === 'delete' ? `${confirmDialog.time} randevu saatini silmek istediğinize emin misiniz?` :
                         confirmDialog.action === 'status' ? `${confirmDialog.time} randevu saatinin durumunu ${confirmDialog.isActive ? 'aktif' : 'pasif'} yapmak istediğinize emin misiniz?` :
                         confirmDialog.action === 'delete_working_hours' ? 
                            (confirmDialog.isDefault ? 'Varsayılan çalışma saatlerini sıfırlamak istediğinize emin misiniz?' : 'Bu özel günü silmek istediğinize emin misiniz?') :
                         confirmDialog.action === 'delete_all_working_hours' ? 'Tüm özel günleri kaldırmak istediğinize emin misiniz?' :
                         'Tüm randevu saatlerini kaldırmak istediğinize emin misiniz?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button 
                        onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                        fullWidth={isMobile}
                    >
                        İptal
                    </Button>
                    <Button
                        onClick={() => {
                            if (confirmDialog.action === 'delete') handleDelete();
                            else if (confirmDialog.action === 'status') handleStatusChange();
                            else if (confirmDialog.action === 'delete_working_hours') handleDeleteWorkingHours(confirmDialog.id!);
                            else if (confirmDialog.action === 'delete_all_working_hours') handleDeleteAllWorkingHours();
                            else if (confirmDialog.action === 'delete_all_time_slots') handleDeleteAllTimeSlots();
                        }}
                        color="error"
                        variant="contained"
                        fullWidth={isMobile}
                    >
                        Onayla
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TimeSlotsAdmin; 